/**
 * SheetModelCache — the client-scoped cache of opened rows-side sheets and
 * the "open a sheet" facade over it (not the `SheetRegistries` lookup tables).
 *
 * Owns two pieces of shared state:
 *
 *   1. `Map<sheetKey, Sheet>` — caches hydrated sheets so repeated
 *      `open(key)` calls return the same instance (identity semantics
 *      for consumers that register subscribers against the sheet).
 *   2. `SourceRow.CellDataStore<string>` — the Client-scoped
 *      `canonicalCells` store. Every sheet hydrated through this
 *      accessor shares one canonical store, so two sheets that each
 *      declare the same `:~`-keyed provider row read identical
 *      cell data and de-dup the provider fetch.
 *
 * Callers do:
 *
 * ```ts
 * const accessor = new SheetModelCache({ backend });
 * const sheetA = await accessor.open("sheet-1");
 * const sheetB = await accessor.open("sheet-2");
 * // sheetA + sheetB share accessor.canonicalCells
 * ```
 *
 * Concurrent `open(key)` calls dedup via an in-flight map — the
 * second caller awaits the first's fetch instead of issuing a
 * duplicate `backend.fetchSheet`.
 *
 * The accessor is deliberately minimal — no CRUD / ACL surface.
 * `@tento/lona`'s `SheetsClient` (`client.sheets`) keeps those concerns; the
 * lona-js `Client` reaches it through `platformOf(client).sheet` /
 * `ephemeralSheet`, which delegate the hydration half to this class.
 */

import type { RecomputeScheduler } from "./sheet-recompute-queue.ts";
import { ListenerErrors, type ListenerErrorSink } from "@tento-core/listener-errors";
import { Uuid } from "@tento-core/uuid";
import type { Option } from "../core/option.ts";
import { SheetRegistries } from "./sheet-registries.ts";
import type { NaiveDate } from "@tento-chrono";
import { Ledger } from "@tento-core/ledger";
import { TransactionLog } from "@tento-core/transaction-log";

import type { AliasTemplate, BuiltinAliasFactory } from "../aliases/alias-templates.ts";
import { wireDeclaredReexpand } from "../aliases/alias-templates.ts";
import { AddressOf } from "../ids/address-of.ts";
import type { Backend, BatchRowCells } from "../backends/rows-backend.ts";
import type { SyncPoll } from "../sync/sync-state.ts";
import {
  ephemeralFromCatalog,
  isBuiltinSheetKey,
} from "../raw/ephemeral-sheet-catalog.ts";
import type { InvalidationSink } from "../tx/invalidation.ts";
import { LedgerBumper, LedgerKeys } from "../stores/ledger-keys.ts";
import type { CellLedgerBump, SheetLedger } from "../stores/ledger-keys.ts";
import { RawSheet, type RawRow, hydrateSheet } from "../raw/raw-sheet.ts";
import { RowLocalId } from "../ids/row-local-id.ts";
import type { Sheet } from "./sheet.ts";
import { SheetId } from "../ids/sheet-id.ts";
import { sheetInternals, sheetWiring, type RequestFailureReporter } from "./sheet-stores.ts";
import { CellDataStore } from "../stores/source-row-cell-data-store.ts";
import { SheetTxRecorder } from "../tx/tx-log.ts";
import { RowWrites } from "../ledger/row-writes.ts";
import type { SheetTxOp } from "../tx/tx-log.ts";
import { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";

/**
 * **Seam 12 (sdk-0 C5)** — how an injected canonical cell store gets its ledger
 * bump. The dep used to be a bare `CellDataStore<string>`, and a store built
 * outside the accessor carries no `cellBump`: its writes land without moving the
 * `data:` versions LR6's render skip compares, so a stale row can be authorised
 * to skip its repaint. The store's bump is fixed at construction, so "carrying
 * its bump" means the accessor must be the one that constructs it — and the only
 * alternative, a store with no bump at all, must be *said*, never reached by
 * leaving a field out.
 */
export type CanonicalCellsInjection =
  | {
    /**
     * Instrumented: the ACCESSOR owns the bump. `build` is invoked exactly once,
     * during construction, with the accessor's own `CellLedgerBump<string>`
     * (its bumper + the `data:<key>` node mapper); hand it straight to
     * `new CellDataStore<string>({ cellBump })`. Seed the store inside `build`
     * if a pre-seeded one is wanted — the returned instance IS the accessor's
     * `canonicalCells`. A canonical write therefore always moves the versions
     * LR6 compares.
     */
    readonly bump: "accessor";
    readonly build: (cellBump: CellLedgerBump<string>) => CellDataStore<string>;
  }
  | {
    /**
     * Explicitly un-instrumented: a raw store whose writes bump nothing on this
     * accessor's ledger. Legitimate for fixtures that never consult LR6, and for
     * a store shared with a *different* accessor (whose bumper it already
     * carries) — but it is a choice, and `reason` records whose. The accessor
     * refuses an empty reason at construction.
     */
    readonly bump: "none";
    readonly store: CellDataStore<string>;
    readonly reason: string;
  };

export interface SheetModelCacheDeps {
  readonly backend: Backend;
  /**
   * Where a throw of a subscriber of the accessor's cell stores goes (its
   * client's `ClientConfig.listenerErrorHandler`): the ordinary store and the
   * canonical store it builds itself (an injected canonical store brings its
   * own). Absent — `ListenerErrors.RETHROW`.
   */
  readonly listenerErrors?: ListenerErrorSink;
  /**
   * Shared canonical cell-data store. Every sheet hydrated through
   * the accessor routes `:~`-keyed (canonical / provider) rows'
   * cells here so reads dedup across sheets. Omitted, the accessor
   * builds a fresh instrumented store; supply a `CanonicalCellsInjection`
   * to control construction (and to state, if that is the intent, that
   * the store carries no bump — see that type).
   */
  readonly canonicalCells?: CanonicalCellsInjection;
  /**
   * App-supplied builtin alias factories (R6.2 — client-hydrated
   * aliases). Each factory ships an `AliasTemplate`; dependency-driven
   * re-expansion is declared via `AliasTemplate.reexpandOn` (the
   * accessor wires every factory through `wireDeclaredReexpand`, so a
   * declared path pattern subscribes the sheet's invalidation sink and
   * calls `sheet.reexpandAliasesByTemplate(name)` on a match). The
   * optional `onAttach(sheet)` hook remains for behavior beyond that.
   * The rows-side stays preferences-agnostic; lona-js / app code owns
   * the binding between its data and the alias.
   */
  readonly builtinAliases?: readonly BuiltinAliasFactory[];
  /**
   * How much of a computed-row recompute every sheet the accessor opens runs
   * per tick: the host's frame-budget port, one per client. Absent — all of
   * it, synchronously (the immediate default).
   */
  readonly recomputeScheduler?: RecomputeScheduler;
  readonly requestFailureReporter?: RequestFailureReporter;
  /**
   * The lookup tables every sheet the accessor opens resolves its rows
   * through (`Sheet.registries`): its client's `ClientConfig.registries`.
   * Absent — the built-ins (`SheetRegistries.BUILTINS`).
   */
  readonly registries?: SheetRegistries;
  /**
   * Shared RowLocalId registry. When supplied, every sheet opened
   * through the accessor interns its row ids into this registry,
   * so cross-sheet references (via `RowKey.of` + resolve)
   * compare by reference equality. Defaults to a fresh registry.
   */
  readonly registry?: RowLocalId.Registry;
  /**
   * Shared invalidation sink. When supplied, every sheet's store
   * mutations fan out through this sink — useful when the Client
   * wants a single observer stream across all open sheets.
   * Defaults to a fresh `InvalidationSink.create()` per sheet.
   */
  readonly invalidationSink?: InvalidationSink;
  /**
   * Propagated to `hydrateSheet` — caller opts out of the
   * built-in decoder registration when they want to register a
   * custom set. See `encodings/builtin.ts`.
   */
  readonly skipBuiltinEncodings?: boolean;
  /**
   * Injected sync-status poll capability (see `SyncPoll`). Threaded to
   * every hydrated sheet's `Sheet.syncPoll` so `ListDataSource` can poll
   * a batch-fetch's in-flight background syncs and re-fetch on completion.
   */
  readonly syncPoll?: SyncPoll;
  /**
   * TX0 — the frame-close scheduler for the transaction log's implicit
   * per-frame txn. Production passes the fastdom mutate phase
   * (`(flush) => $$.mutate(flush)`) so frame boundaries align with rAF; the
   * default batches to a microtask. Tests inject a manual flush.
   */
  readonly txScheduler?: TransactionLog.Scheduler;
}

/**
 * The client's cache of opened rows-side sheets, one per sheet key, and the open verb over it;
 * `LonaSdk.Client` owns one.
 */
/**
 * How much of its transaction log a `SheetModelCache` keeps: the newest
 * `transactions` sealed txns, fewer when they hold more than `ops` ops
 * together (the newest is always kept). `TxFrameJoin` reads the txns sealed
 * since its previous flush — one frame's worth — and `client.txLog` answers
 * this window; `counters()` stay all-time.
 */
export const TX_LOG_RETAIN = Object.freeze({ transactions: 256, ops: 8192 });

export class SheetModelCache {
  readonly #sheets = new Map<SheetNetworkKey, Sheet>();
  readonly #inFlight = new Map<SheetNetworkKey, Promise<Sheet>>();
  /**
   * The client-scoped row-identity ledger (row-identity ledger, L2). One
   * instance shared across every sheet this accessor opens — symmetric with
   * `canonicalCells` — so cross-sheet canonical writes land on one node
   * (§1.5). Runs **dark** in L2: writes bump versions, nothing consumes them
   * yet. Bound to a registry scope epoch (rebuilt at login, OQ12).
   */
  readonly ledger: SheetLedger;
  /** The single write-side handle onto `ledger` (+ the `ledger/bumps` counters). */
  readonly bumper: LedgerBumper;
  /**
   * The client-scoped transaction log (TX0). One instance shared across every
   * sheet this accessor opens — the recorder rides `bumper`, so every §7.1
   * store bump on any sheet appends here. Runs **dark**: writes record ops,
   * nothing reads the log to make a decision yet.
   */
  readonly txLog: TransactionLog<SheetTxOp>;
  readonly canonicalCells: CellDataStore<string>;
  /** Ordinary persisted facts, owned by the same client identity scope as canonical cells. */
  readonly ordinaryCells: CellDataStore<RowLocalId>;
  /**
   * Seam 12 — which bump choice `canonicalCells` was built with. `"accessor"`
   * means a canonical write moves this accessor's ledger (the versions LR6
   * compares); `"none"` means the caller explicitly waived that. Read by tests
   * and by anyone diagnosing a skip that should not have been authorised.
   */
  readonly canonicalCellsBump: "accessor" | "none";
  readonly #backend: Backend;
  readonly #registry: Option<RowLocalId.Registry>;
  readonly #invalidationSink: Option<InvalidationSink>;
  readonly #skipBuiltinEncodings: boolean;
  readonly #builtinAliases: Option<readonly BuiltinAliasFactory[]>;
  readonly #recomputeScheduler: Option<RecomputeScheduler>;
  readonly #requestFailureReporter: RequestFailureReporter;
  readonly #syncPoll: Option<SyncPoll>;
  /** The lookup tables every sheet this accessor opens resolves through. */
  readonly registries: SheetRegistries;
  /**
   * The client's ONE row-field ledger (`RowWrites`): every write of a row's
   * own fields — from any sheet this accessor opens, and from the SDK's row
   * facades — enters it, keyed by `RowLocalId`, so a row held by two sheets
   * (or by none) has one pending model. Its rows-side display is every open
   * sheet holding the row; the SDK registers its own row copy beside it.
   */
  readonly rowWrites: RowWrites;

  constructor(deps: SheetModelCacheDeps) {
    this.#requestFailureReporter = deps.requestFailureReporter ?? ((_error, report) => report());
    this.#backend = deps.backend;
    // One announce per row write: the shared sink when the client gave one,
    // else each holding sheet's own sink once.
    this.rowWrites = new RowWrites((row) => {
      if (this.#invalidationSink != null) return [this.#invalidationSink];
      const sinks = new Set<InvalidationSink>();
      for (const sheet of this.#holding(row)) sinks.add(sheet.invalidationSink);
      return [...sinks];
    });
    this.rowWrites.addDisplay({
      peek: (row) => {
        for (const sheet of this.#holding(row)) return sheetInternals(sheet).peekRowFields(row);
        return null;
      },
      show: (row, delta) => {
        const extra: string[] = [];
        for (const sheet of this.#holding(row)) {
          for (const field of sheetInternals(sheet).showRowFields(row, delta)) {
            if (!extra.includes(field)) extra.push(field);
          }
        }
        return extra;
      },
    }, "last");
    this.ledger = new Ledger<NaiveDate.Range>();
    this.bumper = new LedgerBumper(this.ledger);
    // TX0: construct the log + recorder and ride it on the bumper (dark). The
    // recorder owns the log; expose the log for inspection. Default scheduler
    // batches the implicit frame txn to a microtask.
    const txScheduler = deps.txScheduler ?? ((flush) => queueMicrotask(flush));
    // Bounded (judge P4): one log per client, and every cell set of every
    // sheet it opens is an op, so an unbounded log kept each open's hydrate
    // and fetch ops for the client's lifetime (+2.9 MB per open/dispose at
    // 25x365). Its readers only look back a frame (`TxFrameJoin` brackets
    // the txns sealed since its last flush) or at the recent window
    // (`client.txLog`); see `TX_LOG_RETAIN`.
    const recorder = new SheetTxRecorder({
      scheduler: txScheduler,
      retain: TX_LOG_RETAIN.transactions,
      retainOps: TX_LOG_RETAIN.ops,
      retainOpsPolicy: "strict",
    });
    this.bumper.attachRecorder(recorder);
    const listenerErrors = deps.listenerErrors ?? ListenerErrors.RETHROW;
    this.ordinaryCells = new CellDataStore<RowLocalId>({
      capacity: 512, maxCells: 50_000, listenerErrors,
      cellBump: { bumper: this.bumper, bumpKeyFor: id => LedgerKeys.data(AddressOf.forLocalId(id)) },
    });
    this.txLog = recorder.log;
    // The canonical store keys by raw canonical lookup key; its `data:` node is
    // that key's address (`AddressOf.forLookupKey(k)`) — the SAME node a
    // per-sheet canonical row resolves to via `AddressOf`, so cross-sheet
    // writes to one `:~` key share one version (canonical-share, §1.5).
    //
    // Seam 12: the bump belongs to the accessor, so the accessor constructs the
    // store — either itself (no injection) or through the caller's `build`,
    // handing it this exact bump. The only way to end up with an un-instrumented
    // canonical store is to ask for one by name.
    const cellBump: CellLedgerBump<string> = {
      bumper: this.bumper,
      bumpKeyFor: (k) => LedgerKeys.data(AddressOf.forLookupKey(k)),
    };
    const injection = deps.canonicalCells ?? null;
    if (injection == null) {
      this.canonicalCells = new CellDataStore<string>({ cellBump, listenerErrors });
      this.canonicalCellsBump = "accessor";
    } else if (injection.bump === "accessor") {
      this.canonicalCells = injection.build(cellBump);
      this.canonicalCellsBump = "accessor";
    } else {
      if (injection.reason.trim().length === 0) {
        throw new Error(
          "SheetModelCache: canonicalCells { bump: \"none\" } must carry a " +
            "non-empty `reason`. An un-instrumented canonical store lets a " +
            "canonical write land without moving the `data:` versions LR6's " +
            "render skip compares, so a stale row can be authorised to skip " +
            "its repaint (seam 12 / sdk-0 C5). Use { bump: \"accessor\", build } " +
            "unless you mean it.",
        );
      }
      this.canonicalCells = injection.store;
      this.canonicalCellsBump = "none";
    }
    this.#registry = deps.registry ?? null;
    this.#invalidationSink = deps.invalidationSink ?? null;
    this.#skipBuiltinEncodings = deps.skipBuiltinEncodings ?? false;
    this.#recomputeScheduler = deps.recomputeScheduler ?? null;
    this.#builtinAliases = deps.builtinAliases != null
      ? deps.builtinAliases.map(wireDeclaredReexpand)
      : null;
    this.#syncPoll = deps.syncPoll ?? null;
    this.registries = deps.registries ?? SheetRegistries.BUILTINS;
  }

  /**
   * Resolve a registered builtin alias template by name — the seam the
   * client's parse-boundary normalization uses to resolve a row's data
   * address generically (`AliasTemplate.dataRef`, data-address
   * migration Phase B) without hardcoding per-type key logic. `null`
   * when this accessor registers no such template.
   */
  aliasTemplate(templateName: string): Option<AliasTemplate> {
    for (const factory of this.#builtinAliases ?? []) {
      if (factory.templateName === templateName) return factory.template;
    }
    return null;
  }

  /**
   * Open a sheet by key. Subsequent calls with the same key return
   * the cached instance; concurrent calls share one in-flight
   * fetch + hydration.
   */
  async open(sheetKey: SheetNetworkKey): Promise<Sheet> {
    const cached = this.#sheets.get(sheetKey);
    if (cached) return cached;
    const pending = this.#inFlight.get(sheetKey);
    if (pending) return pending;

    const next = this.#fetchAndHydrate(sheetKey);
    this.#inFlight.set(sheetKey, next);
    try {
      const sheet = await next;
      this.#sheets.set(sheetKey, sheet);
      return sheet;
    } finally {
      this.#inFlight.delete(sheetKey);
    }
  }

  /**
   * Open an ephemeral sheet — client-minted UUID, no server-side
   * record. `Sheet.ephemeral === true`; batch-prefetch routes
   * around the sheet-scoped endpoint.
   *
   * For the symmetric "I already have the server-fetched wire form,
   * hydrate it without re-fetching" case, use `hydrate(raw)` —
   * that sheet IS server-backed (`Sheet.ephemeral === false`).
   *
   * Cached under `raw.id` so subsequent `open(raw.id)` calls return
   * the same instance. Collisions throw.
   */
  openEphemeral(raw: RawSheet): Sheet {
    return this.#installHydrated(raw, true);
  }

  /**
   * Register a pre-fetched server-backed RawSheet in the accessor
   * without going through the network. Used by callers that already
   * hold the wire form (e.g. `SheetHandle.parse` on the lona-js side).
   *
   * `Sheet.ephemeral === false` — the sheet exists server-side and
   * batch endpoints are valid.
   */
  hydrate(raw: RawSheet): Sheet {
    return this.#installHydrated(raw, false);
  }

  #installHydrated(raw: RawSheet, ephemeral: boolean): Sheet {
    // Builtin identity comes from the sheet's own reserved key when it has one:
    // a PROMOTED builtin is installed under its server id (`ss_<hex>`), which
    // names no builtin, so normalizing against `raw.id` alone left the promoted
    // Journal sheet unrecognized and its 400px row height unrestored.
    const normalized = normalizeBuiltinRows(raw, raw.lookupKey ?? raw.id);
    if (this.#sheets.has(normalized.id)) {
      throw new Error(
        `SheetModelCache: sheet "${normalized.id}" already open. Call ` +
          `forget(raw.id) first if you intend to replace.`,
      );
    }
    const sheet = hydrateSheet(normalized, this.#hydrateDeps(ephemeral ? "ephemeral" : "server-backed"));
    sheetWiring(sheet).setRequestFailureReporter(this.#requestFailureReporter);
    this.#sheets.set(normalized.id, sheet);
    return sheet;
  }

  /**
   * Return the cached sheet for `sheetKey` without fetching. `null`
   * if the accessor hasn't opened it yet.
   */
  peek(sheetKey: SheetNetworkKey): Option<Sheet> {
    return this.#sheets.get(sheetKey) ?? null;
  }

  /**
   * The cached sheet `id` names, under whichever spelling it was opened
   * with (`ss_<hex>`, the UUID, an ephemeral spec's `ephemeral(<name>)`);
   * `null` if the accessor holds none. Every spelling of a sheet id parses
   * to the one interned `SheetId`, so the spellings compare by identity.
   */
  peekById(id: SheetId): Option<Sheet> {
    const direct = this.#sheets.get(SheetNetworkKey.fromWire(id.toString())) ?? null;
    if (direct != null) return direct;
    for (const [key, sheet] of this.#sheets) {
      if (SheetId.parse(key) === id) return sheet;
    }
    return null;
  }

  /**
   * Retire the row-identity ledger's per-row nodes of `rows` (the client
   * released them: no open sheet places them): each row's `data:` node and
   * its fetch-coverage node, with their edges and versions. Without it every
   * row a client ever opened kept its nodes for the client's life, a linear
   * heap growth over open/close cycles. A row opened again mints its nodes
   * anew (a fresh sheet's render cursors start over with it).
   *
   * A lookup-form row's data node is not retired: it is a global name
   * (`data:<lookup key>`), the node every row resolving to that key shares,
   * in any sheet (`AddressOf.resolve` rule 2). Retiring it with one sheet's
   * row deleted the versions and cut the edges of an open sheet that reads
   * the same key. Those nodes are bounded by the distinct
   * keys, not by open/close cycles.
   */
  retireRowNodes(rows: Iterable<RowLocalId>): void {
    for (const id of rows) {
      if (!id.key.isLookup() && !id.key.isReserved()) this.ledger.retireNode(LedgerKeys.data(AddressOf.forLocalId(id)));
      this.ledger.retireNode(LedgerKeys.coverage(id.serial));
    }
  }

  /**
   * Drop the cached sheet for `sheetKey`. The canonical store
   * stays — its cells are shared across every sheet the accessor
   * has ever opened. Callers that need to invalidate canonical
   * entries reach through `this.canonicalCells.forget(lookupKey)`
   * directly.
   */
  forget(sheetKey: SheetNetworkKey): void {
    this.#sheets.delete(sheetKey);
  }

  /**
   * Re-key every row in the cached sheet `sheetKey` from its current
   * (ephemeral) raw id to a server-minted raw id, given an
   * ephemeral→server raw-id map.
   *
   * Used by `SheetHandle.applyPromotion` after an ephemeral→server
   * promote: the server mints fresh row ids, but the rows-side `Sheet`
   * is reused (the sheet id is preserved, so `peek` hits) and still
   * keys its rows under the old ephemeral ids — leaving its tree walking
   * rows the promoted handle's ordered tree no longer knows, which is
   * what reversed/desynced the sheet after a claim. Re-keying realigns
   * the two id namespaces without minting ids on the client (the server
   * stays the sole id authority).
   *
   * Re-keys IN PLACE — NOT serialize→dispose→hydrate. For each
   * ephemeral→server pair it re-points the SAME `RowLocalId` via
   * `Registry.rekey`: aliases the server raw id to the existing local id
   * (inbound resolve) and remaps that local id's authoritative key (outbound
   * server ops). Because every store keys by `RowLocalId` reference and
   * `canonicalCells` keys by lookupKey — neither of which changes — all cells
   * (incl. canonical seed data), live cell subscriptions, and rendered DOM
   * survive untouched. So the promote is invisible: no rebuild, no re-render,
   * no orphaned subscriptions, no dropped seed cells. Then flips the sheet
   * server-backed (`markPromoted`) so later batch prefetch uses the sheet
   * endpoint. No-op (returns the cached sheet unchanged) when the sheet isn't
   * cached or the map is empty — e.g. a promote whose response carried no rows,
   * which then leans on the position-resolution fallback.
   */
  rekeyRows(
    sheetKey: SheetNetworkKey,
    idRemap: ReadonlyMap<string, string>,
  ): Option<Sheet> {
    const existing = this.#sheets.get(sheetKey);
    if (existing == null || idRemap.size === 0) return existing ?? null;
    for (const [fromRaw, toRaw] of idRemap) {
      existing.registry.rekey(fromRaw, toRaw);
    }
    sheetInternals(existing).markPromoted();
    return existing;
  }

  /** Count of currently cached sheets. */
  size(): number {
    return this.#sheets.size;
  }

  /** Flush page-local pending cell writes on every cached sheet. */
  flushPendingCellWrites(): void {
    for (const sheet of this.#sheets.values()) sheet.flushPendingCellWrites();
  }

  /**
   * Batch fetch cells for a client-supplied rowKey list, scoped to
   * `sheetKey`. Required for aggregate rows (calendar-list,
   * task-list) whose dynamic children are computed from client-side
   * state — the server can't enumerate them from sheet membership
   * alone.
   *
   * Returns the per-row outcomes verbatim; the caller decides how
   * to fan them out to row stores (Phase B work — `ListDataSource`
   * and the CLI render loop are the first consumers). The accessor
   * deliberately does not mutate any store here so the seam stays
   * narrow and testable.
   */
  fetchCellsForRows(
    sheetKey: SheetNetworkKey,
    rowKeys: readonly RowNetworkKey[],
    range: NaiveDate.Range,
  ): Promise<Map<RowNetworkKey, BatchRowCells>> {
    return this.#backend.fetchCellsBatch(sheetKey, rowKeys, range);
  }

  /** The deps every sheet this cache hydrates shares; `mode` is the one that differs. */
  #hydrateDeps(mode: RawSheet.HydrateDeps.Mode): RawSheet.HydrateDeps {
    return {
      backend: this.#backend,
      registry: this.#registry,
      invalidationSink: this.#invalidationSink,
      canonicalCells: this.canonicalCells,
      ordinaryCells: this.ordinaryCells,
      builtinAliases: this.#builtinAliases ?? [],
      recomputeScheduler: this.#recomputeScheduler,
      registries: this.registries,
      encodings: this.#skipBuiltinEncodings ? "custom" : "builtin",
      registerEncodings: null,
      mode,
      syncPoll: this.#syncPoll,
      ledgerBumper: this.bumper,
      rowWrites: this.rowWrites,
    };
  }

  /** The open sheets holding row `row`, once each. */
  *#holding(row: RowLocalId): Generator<Sheet> {
    const seen = new Set<Sheet>();
    for (const sheet of this.#sheets.values()) {
      if (seen.has(sheet) || sheet.peekRow(row) == null) continue;
      seen.add(sheet);
      yield sheet;
    }
  }

  /** This cache's own pre-principal scope, for a cache with no registry (`#ownerScope`). */
  #sessionScope: Option<string> = null;

  /** The principal a catalog miss derives under: the registry's, else this cache's own session scope. */
  #ownerScope(): string {
    if (this.#registry != null) return this.#registry.ownerScope;
    this.#sessionScope ??= `session:${Uuid.make()}`;
    return this.#sessionScope;
  }

  async #fetchAndHydrate(sheetKey: SheetNetworkKey): Promise<Sheet> {
    // Server-FIRST: a user who already promoted this builtin gets the persisted
    // record (server id, server rows) and never re-derives an id. The catalog
    // is only the miss path, and it derives under THIS client's principal —
    // the registry's `ownerScope`: the signed-in user, or before login the
    // client's own session scope — so no two accounts (or clients) hydrate the
    // same global `spreadsheets.id`.
    const raw = await this.#backend.fetchSheet(sheetKey);
    const effective = raw ??
      ephemeralFromCatalog(sheetKey, this.#ownerScope());
    if (effective == null) {
      throw new Error(
        `SheetModelCache.open: sheet "${sheetKey}" not found and no ` +
          `entry in the reserved-sheet catalog. Caller should either ` +
          `use a persisted sheetKey or open via openEphemeral.`,
      );
    }
    const sheet = hydrateSheet(
      normalizeBuiltinRows(effective, sheetKey),
      this.#hydrateDeps(raw == null ? "ephemeral" : "server-backed"),
    );
    sheetWiring(sheet).setRequestFailureReporter(this.#requestFailureReporter);
    return sheet;
  }
}

const JOURNAL_SHEET_LOOKUP_KEY = ":journal";
const JOURNAL_ROW_LOOKUP_KEY = ":~lona:journal";
const JOURNAL_ROW_LABEL = "Journal";
const JOURNAL_ROW_HEIGHT_PX = 400;
const JOURNAL_SHEET_WIDTH_PX = 300;
const JOURNAL_MOOD_DEFAULT_ATTRIBUTES: {
  readonly unit: string;
  readonly prompt: string;
} = {
  unit: "scale1-5",
  prompt: "rate your mood (1-5)",
};

function normalizeBuiltinRows(raw: RawSheet, sheetKey: string): RawSheet {
  const lookupKey = builtinLookupKeyForRawSheet(raw, sheetKey);
  if (lookupKey !== JOURNAL_SHEET_LOOKUP_KEY) return raw;

  let changed = false;
  const width = raw.width ?? JOURNAL_SHEET_WIDTH_PX;
  if (raw.width == null) changed = true;
  // `:~lona:journal` is a per-user SINGLETON, so at most one row may be given
  // the key back. A second journal-shaped row keeps whatever it was saved with
  // rather than becoming a duplicate claimant of the same canonical cells.
  let journalKeyClaimed = false;
  const rows = raw.rows.map((row) => {
    const claimsJournalKey = isJournalSourceRawRow(row);
    const normalized = claimsJournalKey && journalKeyClaimed
      ? row
      : normalizeJournalBuiltinRawRow(row);
    if (claimsJournalKey) journalKeyClaimed = true;
    if (normalized !== row) changed = true;
    return normalized;
  });
  if (!changed) return raw;
  return { ...raw, width, rows };
}

function builtinLookupKeyForRawSheet(
  raw: RawSheet,
  sheetKey: string,
): Option<string> {
  if (isBuiltinSheetKey(raw.lookupKey ?? null)) return raw.lookupKey ?? null;
  if (isBuiltinSheetKey(sheetKey)) return sheetKey;
  if (raw.title === "Journal" && hasJournalBuiltinRawRow(raw.rows)) {
    return JOURNAL_SHEET_LOOKUP_KEY;
  }
  return null;
}

function hasJournalBuiltinRawRow(rows: readonly RawRow[]): boolean {
  for (const row of rows) {
    if (isJournalSourceRawRow(row)) return true;
  }
  return false;
}

/**
 * The Journal builtin's source row — by its reserved key, or (when a promote
 * came back without one) by its authored shape. The key is the row's stable
 * identity, so a row that lost it is repaired rather than skipped: without
 * this, `normalizeBuiltinRows` left the row keyless AND at the default height.
 */
function isJournalSourceRawRow(row: RawRow): boolean {
  if (row.lookupKey === JOURNAL_ROW_LOOKUP_KEY) return true;
  return row.lookupKey == null &&
    row.type === "journal" &&
    row.label === JOURNAL_ROW_LABEL;
}

function normalizeJournalBuiltinRawRow(row: RawRow): RawRow {
  if (isJournalSourceRawRow(row)) {
    if (
      row.lookupKey === JOURNAL_ROW_LOOKUP_KEY &&
      row.heightPx === JOURNAL_ROW_HEIGHT_PX &&
      row.timeScale === "day"
    ) {
      return row;
    }
    return {
      ...row,
      lookupKey: JOURNAL_ROW_LOOKUP_KEY,
      heightPx: JOURNAL_ROW_HEIGHT_PX,
      timeScale: "day",
    };
  }

  if (!isJournalMoodRawRow(row)) return row;
  const attributes = row.attributes;
  const hasCanonicalAttributes =
    attributes != null &&
    attributes.unit === "scale1-5" &&
    typeof attributes.prompt === "string";
  if (row.timeScale === "day" && hasCanonicalAttributes) return row;
  return {
    ...row,
    timeScale: "day",
    attributes: {
      ...JOURNAL_MOOD_DEFAULT_ATTRIBUTES,
      ...(attributes ?? {}),
      unit: "scale1-5",
    },
  };
}

function isJournalMoodRawRow(row: RawRow): boolean {
  if (row.lookupKey != null) return false;
  return row.type === "number" && row.label === "Mood";
}
